import { useMemo, useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Calendar, User, Truck, AlertTriangle, Check, Search, Loader2 } from 'lucide-react';
import { vehicleService } from '../services/vehicleService';
import { driverService } from '../services/driverService';

interface VehicleAssignment {
  id: string;
  vehicleId: string;
  vehiclePlate: string;
  personnelId: string;
  personnelName: string;
  startDate: string;
  endDate: string;
  startKm?: number;
  endKm?: number;
  fuelLevel?: number;
  status: 'active' | 'completed' | 'cancelled';
  notes?: string;
  createdAt: string;
  completedAt?: string;
}

interface Personnel {
  id: string;
  name: string;
  position: string;
  department: string;
  licenseType?: string;
}

interface Vehicle {
  id: string;
  plate: string;
  brand: string;
  model: string;
  year: number;
  status: string;
}

const mapApiAssignmentToUi = (assignment: any): VehicleAssignment => ({
  id: String(assignment.id),
  vehicleId: String(assignment.vehicle_id || assignment.vehicleId || ''),
  vehiclePlate: assignment.vehiclePlate || assignment.cekici_plaka || assignment.vehicle_plate || '-',
  personnelId: String(assignment.driver_id || assignment.personnelId || ''),
  personnelName: assignment.personnelName || assignment.driver_name || '-',
  startDate: assignment.assigned_date_text || assignment.assigned_date || assignment.assignedDate || assignment.startDate || '',
  endDate: assignment.return_date_text || assignment.return_date || assignment.returnDate || assignment.endDate || '',
  startKm: assignment.start_km || assignment.startKm || undefined,
  endKm: assignment.end_km || assignment.endKm || undefined,
  fuelLevel: assignment.fuel_cost || assignment.fuelLevel || undefined,
  status: assignment.status || 'active',
  notes: assignment.notes || undefined,
  createdAt: assignment.created_at || assignment.createdAt || new Date().toISOString(),
  completedAt: assignment.completed_at || assignment.completedAt || undefined,
});

export function VehicleAssignments() {
  const [assignments, setAssignments] = useState<VehicleAssignment[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<VehicleAssignment | null>(null);
  const [formData, setFormData] = useState<Partial<VehicleAssignment>>({
    status: 'active',
  });
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedPersonnel, setSelectedPersonnel] = useState('');
  const [conflictWarning, setConflictWarning] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicleLookup, setVehicleLookup] = useState('');
  const [personnelLookup, setPersonnelLookup] = useState('');
  const [activeLookup, setActiveLookup] = useState<'vehicle' | 'personnel' | null>(null);

  const getVehicleLabel = (vehicle?: Vehicle) => vehicle ? `${vehicle.plate} - ${vehicle.brand} ${vehicle.model}`.trim() : '';
  const getPersonnelLabel = (person?: Personnel) => person ? `${person.name}${person.licenseType ? ` (${person.licenseType})` : ''}` : '';

  const filteredVehicleOptions = useMemo(() => {
    const query = vehicleLookup.trim().toLocaleLowerCase('tr-TR');
    if (!query) return vehicles.slice(0, 20);
    return vehicles.filter((vehicle) => getVehicleLabel(vehicle).toLocaleLowerCase('tr-TR').includes(query)).slice(0, 20);
  }, [vehicles, vehicleLookup]);

  const filteredPersonnelOptions = useMemo(() => {
    const query = personnelLookup.trim().toLocaleLowerCase('tr-TR');
    if (!query) return personnel.slice(0, 20);
    return personnel.filter((person) => getPersonnelLabel(person).toLocaleLowerCase('tr-TR').includes(query)).slice(0, 20);
  }, [personnel, personnelLookup]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.vehicleId && formData.personnelId && formData.startDate && formData.endDate) {
      checkConflicts();
    } else {
      setConflictWarning('');
    }
  }, [formData.vehicleId, formData.personnelId, formData.startDate, formData.endDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [assignmentsRes, vehiclesRes, usersRes] = await Promise.all([
        vehicleService.getAssignments(),
        vehicleService.getVehicles(),
        driverService.getDrivers({ type: 'internal' })
      ]);

      if (assignmentsRes.success && assignmentsRes.data) {
        setAssignments(assignmentsRes.data.map(mapApiAssignmentToUi));
      }

      if (vehiclesRes.success && vehiclesRes.data) {
        setVehicles(vehiclesRes.data.map((v: any) => ({
          id: String(v.id),
          plate: v.cekici_plaka || v.plate || '-',
          brand: v.brand || '',
          model: v.model || '',
          year: Number(v.year || 0),
          status: v.status || '',
        })));
      }

      if (usersRes.success && usersRes.data) {
        const allDrivers = usersRes.data.map((u: any) => ({
          id: String(u.id),
          name: u.full_name || u.name || u.username || '-',
          position: u.driver_type === 'internal' ? 'internal-driver' : 'external-driver',
          department: u.department_name || u.department || '-',
          licenseType: u.license_class || u.licenseType || undefined,
        }));

        setPersonnel(allDrivers);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const checkConflicts = () => {
    if (!formData.vehicleId || !formData.startDate || !formData.endDate) return;

    const vehicleConflict = assignments.find(a => {
      if (editingItem && a.id === editingItem.id) return false;
      if (a.vehicleId !== formData.vehicleId) return false;
      if (a.status === 'cancelled' || a.status === 'completed') return false;

      const aStart = new Date(a.startDate);
      const aEnd = new Date(a.endDate);
      const fStart = new Date(formData.startDate);
      const fEnd = new Date(formData.endDate);
      return (fStart <= aEnd && fEnd >= aStart);
    });

    if (vehicleConflict) {
      setConflictWarning(
        `⚠️ Bu araç ${vehicleConflict.startDate} - ${vehicleConflict.endDate} tarihleri arasında ${vehicleConflict.personnelName} tarafından kullanılıyor!`
      );
      return;
    }

    const driverConflict = assignments.find(a => {
      if (editingItem && a.id === editingItem.id) return false;
      if (a.personnelId !== formData.personnelId) return false;
      if (a.status === 'cancelled' || a.status === 'completed') return false;

      const aStart = new Date(a.startDate);
      const aEnd = new Date(a.endDate);
      const fStart = new Date(formData.startDate!);
      const fEnd = new Date(formData.endDate!);
      return (fStart <= aEnd && fEnd >= aStart);
    });

    if (driverConflict) {
      setConflictWarning(
        `⚠️ Bu sürücünün ${driverConflict.startDate} - ${driverConflict.endDate} tarihleri arasında ${driverConflict.vehiclePlate} üzerinde aktif görevlendirmesi var!`
      );
      return;
    }

    setConflictWarning('');
  };

  const openForm = (item?: VehicleAssignment) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
      setVehicleLookup(getVehicleLabel(vehicles.find((vehicle) => vehicle.id === item.vehicleId)) || item.vehiclePlate || '');
      setPersonnelLookup(getPersonnelLabel(personnel.find((person) => person.id === item.personnelId)) || item.personnelName || '');
    } else {
      setEditingItem(null);
      setFormData({ status: 'active' });
      setVehicleLookup('');
      setPersonnelLookup('');
    }
    setActiveLookup(null);
    setIsFormOpen(true);
    setConflictWarning('');
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData({ status: 'active' });
    setConflictWarning('');
    setVehicleLookup('');
    setPersonnelLookup('');
    setActiveLookup(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (conflictWarning && !window.confirm('Çakışma tespit edildi! Yine de kaydetmek istiyor musunuz?')) {
      return;
    }

    const vehicle = vehicles.find(v => v.id === formData.vehicleId);
    const person = personnel.find(p => p.id === formData.personnelId);

    if (!vehicle || !person) {
      alert('Lütfen araç ve personel seçin');
      return;
    }

    const assignmentData = {
      vehicleId: formData.vehicleId!,
      vehiclePlate: vehicle.plate,
      driverId: formData.personnelId!,
      personnelId: formData.personnelId!,
      personnelName: person.name,
      assignedDate: formData.startDate!,
      startDate: formData.startDate!,
      returnDate: formData.endDate!,
      endDate: formData.endDate!,
      startKm: formData.startKm,
      endKm: formData.endKm,
      fuelCost: formData.fuelLevel,
      fuelLevel: formData.fuelLevel,
      status: formData.status as 'active' | 'completed' | 'cancelled',
      notes: formData.notes,
      assignedBy: 1,
      assignmentType: 'temporary',
    };

    try {
      if (editingItem) {
        const res = await vehicleService.updateAssignment(editingItem.id, assignmentData);
        if (res.success) {
          await loadData();
        }
      } else {
        const res = await vehicleService.createAssignment(assignmentData);
        if (res.success) {
          await loadData();
        }
      }
      closeForm();
    } catch (err) {
      console.error('Error saving assignment:', err);
      alert('Kaydetme sırasında bir hata oluştu');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu atamayı silmek istediğinizden emin misiniz?')) {
      try {
        const res = await vehicleService.deleteAssignment(id);
        if (res.success) {
          setAssignments(prev => prev.filter(item => item.id !== id));
        }
      } catch (err) {
        console.error('Error deleting assignment:', err);
        alert('Silme sırasında bir hata oluştu');
      }
    }
  };

  const handleComplete = async (id: string) => {
    const assignment = assignments.find(a => a.id === id);
    if (!assignment) return;

    const endKm = assignment.endKm || prompt('Bitiş kilometresi girin:', assignment.startKm?.toString());
    
    try {
      const res = await vehicleService.updateAssignment(id, {
        assignedDate: assignment.startDate,
        returnDate: assignment.endDate,
        startKm: assignment.startKm,
        status: 'completed',
        endKm: endKm ? parseInt(endKm) : assignment.endKm,
        fuelCost: assignment.fuelLevel,
        notes: assignment.notes,
        completedAt: new Date().toISOString(),
      });
      
      if (res.success) {
        await loadData();
      }
    } catch (err) {
      console.error('Error completing assignment:', err);
      alert('Güncelleme sırasında bir hata oluştu');
    }
  };

  const filteredAssignments = assignments
    .filter(a => filter === 'all' ? true : a.status === filter)
    .filter(a => {
      if (selectedVehicle && a.vehicleId !== selectedVehicle) return false;
      if (selectedPersonnel && a.personnelId !== selectedPersonnel) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          a.vehiclePlate.toLowerCase().includes(term) ||
          a.personnelName.toLowerCase().includes(term) ||
          a.notes?.toLowerCase().includes(term)
        );
      }
      return true;
    });

  const stats = {
    total: assignments.length,
    active: assignments.filter(a => a.status === 'active').length,
    completed: assignments.filter(a => a.status === 'completed').length,
    cancelled: assignments.filter(a => a.status === 'cancelled').length,
  };

  const getStatusBadge = (status: VehicleAssignment['status']) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Aktif</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Tamamlandı</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">İptal</span>;
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Gecici Gorevlendirmeler</h1>
            <p className="text-sm text-gray-600 mt-1">Belirli tarih araliginda ozmal surucuye gecici cekici gorevlendirmesi yapin</p>
          </div>
          <button
            onClick={() => openForm()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Yeni Atama
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Truck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Toplam Atama</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Aktif</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Tamamlandı</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">İptal</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelled}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arama</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Plaka, personel, not..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="completed">Tamamlandı</option>
              <option value="cancelled">İptal</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Araç</label>
            <select
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tüm Araçlar</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>{v.plate}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personel</label>
            <select
              value={selectedPersonnel}
              onChange={(e) => setSelectedPersonnel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tüm Personel</option>
              {personnel.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Araç</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Personel</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Başlangıç</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Bitiş</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Kilometre</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Yakıt %</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Durum</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{assignment.vehiclePlate}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{assignment.personnelName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {assignment.startDate ? new Date(assignment.startDate).toLocaleDateString('tr-TR') : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {assignment.endDate ? new Date(assignment.endDate).toLocaleDateString('tr-TR') : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {assignment.startKm && (
                        <span>
                          {assignment.startKm.toLocaleString('tr-TR')}
                          {assignment.endKm && ` → ${assignment.endKm.toLocaleString('tr-TR')}`}
                          {assignment.endKm && (
                            <span className="block text-xs text-gray-500">
                              +{(assignment.endKm - assignment.startKm).toLocaleString('tr-TR')} km
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {assignment.fuelLevel && (
                      <span className="text-sm text-gray-900">{assignment.fuelLevel}%</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {getStatusBadge(assignment.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {assignment.status === 'active' && (
                        <button
                          onClick={() => handleComplete(assignment.id)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Tamamla"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => openForm(assignment)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Düzenle"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(assignment.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAssignments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Truck className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500">Atama bulunamadı</p>
          </div>
        )}
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingItem ? 'Atamayı Düzenle' : 'Yeni Atama'}
              </h2>
              <button
                onClick={closeForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {conflictWarning && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">{conflictWarning}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Araç *
                  </label>
                  <input
                    type="text"
                    value={vehicleLookup}
                    onChange={(e) => {
                      setVehicleLookup(e.target.value);
                      setFormData({ ...formData, vehicleId: '' });
                      setActiveLookup('vehicle');
                    }}
                    onFocus={() => setActiveLookup('vehicle')}
                    onBlur={() => window.setTimeout(() => setActiveLookup((current) => current === 'vehicle' ? null : current), 120)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Plaka veya araç bilgisi ara"
                    required
                  />
                  <input type="hidden" value={formData.vehicleId || ''} required />
                  {activeLookup === 'vehicle' && filteredVehicleOptions.length > 0 ? (
                    <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {filteredVehicleOptions.map((vehicle) => (
                        <button
                          key={vehicle.id}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setFormData({ ...formData, vehicleId: vehicle.id });
                            setVehicleLookup(getVehicleLabel(vehicle));
                            setActiveLookup(null);
                          }}
                          className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
                        >
                          {getVehicleLabel(vehicle)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personel *
                  </label>
                  <input
                    type="text"
                    value={personnelLookup}
                    onChange={(e) => {
                      setPersonnelLookup(e.target.value);
                      setFormData({ ...formData, personnelId: '' });
                      setActiveLookup('personnel');
                    }}
                    onFocus={() => setActiveLookup('personnel')}
                    onBlur={() => window.setTimeout(() => setActiveLookup((current) => current === 'personnel' ? null : current), 120)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Sürücü adı ara"
                    required
                  />
                  <input type="hidden" value={formData.personnelId || ''} required />
                  {activeLookup === 'personnel' && filteredPersonnelOptions.length > 0 ? (
                    <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {filteredPersonnelOptions.map((person) => (
                        <button
                          key={person.id}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setFormData({ ...formData, personnelId: person.id });
                            setPersonnelLookup(getPersonnelLabel(person));
                            setActiveLookup(null);
                          }}
                          className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
                        >
                          {getPersonnelLabel(person)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Başlangıç Tarihi *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate || ''}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bitiş Tarihi *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate || ''}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Başlangıç KM
                  </label>
                  <input
                    type="number"
                    value={formData.startKm || ''}
                    onChange={(e) => setFormData({ ...formData, startKm: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bitiş KM
                  </label>
                  <input
                    type="number"
                    value={formData.endKm || ''}
                    onChange={(e) => setFormData({ ...formData, endKm: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yakıt Seviyesi %
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.fuelLevel || ''}
                    onChange={(e) => setFormData({ ...formData, fuelLevel: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum
                </label>
                <select
                  value={formData.status || 'active'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Aktif</option>
                  <option value="completed">Tamamlandı</option>
                  <option value="cancelled">İptal</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notlar
                </label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Atama ile ilgili notlar..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingItem ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
